import { Box } from '@mui/material'
import ButtonMobileComponent from 'components/button-mobile'
import InputMobileComponent from 'components/input-mobile'
import ModalConfirmComponent from 'components/modal-confirm'
import NavComponent from 'components/nav'
import SwitchComponent from 'components/switch'
import { useEffect, useState } from 'react'
import { UpdateSchoolInputProps } from 'types/SchoolTypes'
import { baseURL, MAX_ALLOWED_REGISTRATIONS, MIN_REQUIRED_REGISTRATIONS } from 'utils/constants'
import { GetSubscriptionProps } from 'utils/types'
import SettingsBox from './SettingsBox'
interface ServiceActivationSettingsProps {
  initialCarpoolActive: boolean
  initialSchoolSubscription?: GetSubscriptionProps
  deactivationBlocked: boolean
  onSave: (body: UpdateSchoolInputProps, loadingMsg?: string, cb?: () => void) => Promise<void>
}

function ServiceActivationSettings({
  initialCarpoolActive,
  initialSchoolSubscription,
  deactivationBlocked,
  onSave,
}: ServiceActivationSettingsProps) {
  const { maxRegistrations: initialMaxRegistrations = MAX_ALLOWED_REGISTRATIONS } = initialSchoolSubscription ?? {}
  const [isCarpoolActive, setIsCarpoolActive] = useState(initialCarpoolActive)
  const [maxRegistrations, setMaxRegistrations] = useState<number>(initialMaxRegistrations)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [isLoading, setIsLoading] = useState<string>('')

  // Sync local state with props when props change
  useEffect(() => {
    setMaxRegistrations(initialMaxRegistrations)
  }, [initialMaxRegistrations])

  // hasChanges is always derived
  const hasChanges: boolean = maxRegistrations !== initialMaxRegistrations

  // Handlers
  const handleToggle = () => setConfirmationOpen(true)

  const handleActivationConfirmation = async (confirmed: boolean) => {
    setConfirmationOpen(false)
    if (confirmed) {
      const newServiceState = !isCarpoolActive
      const message = newServiceState ? 'Enabling service...' : 'Disabling service...'
      setIsLoading(message)
      await onSave({ isCarpoolActive: newServiceState }, message, () => {
        setIsCarpoolActive(newServiceState)
        setIsLoading('')
      })
    }
  }

  const handleMaxLimitSubmit = (maxRegistrations: number) => {
    setMaxRegistrations(maxRegistrations)
  }

  const isInvalid: boolean =
    maxRegistrations !== undefined &&
    (maxRegistrations < MIN_REQUIRED_REGISTRATIONS || maxRegistrations > MAX_ALLOWED_REGISTRATIONS)

  const handleSave = async () => {
    if (hasChanges && !isInvalid) {
      const message = 'Updating max registrations...'
      setIsLoading(message)
      await onSave({ schoolSubscription: { maxRegistrations } }, message, () => setIsLoading(''))
    }
  }

  return (
    <SettingsBox title='Service Activation'>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
        {isCarpoolActive ? 'Carpool School Inc. Service Enabled' : 'Enable Carpool School Inc. Service'}
        <SwitchComponent
          isChecked={isCarpoolActive}
          onChange={handleToggle}
          color={isCarpoolActive ? 'secondary' : 'error'}
        />
      </Box>

      <ModalConfirmComponent
        isShow={confirmationOpen}
        title={
          !isCarpoolActive
            ? 'Enable Carpool School Inc. Service?'
            : deactivationBlocked
              ? 'Service Disablement Blocked'
              : 'Disable Carpool School Inc. Service?'
        }
        body={
          !isCarpoolActive ? (
            <>
              By clicking below, you confirm that you have the authority to enable the Carpool School Inc. Service for
              your organization, and you have reviewed and agree to the Carpool School Inc.{' '}
              <span style={{ color: '#beac48', fontWeight: 'bold', textDecoration: 'underline' }}>
                <NavComponent href={`${baseURL}/home/index.html#tos`} text='Terms of Service' isOpenNewTab={true} />
              </span>
              .
            </>
          ) : deactivationBlocked ? (
            `Remove all registered users and unsubscribe first. For help, contact support@carpool.school.`
          ) : (
            'This action will prevent families from using the carpool functionality and may affect existing carpool configurations'
          )
        }
        actionIcon={!isCarpoolActive ? 'yellowPowerIcon' : deactivationBlocked ? 'redBlockIcon' : 'redInfoIcon'}
        actionText={!isCarpoolActive ? 'I Agree and Enable Service' : 'Disable'}
        onActionCancel={() => handleActivationConfirmation(false)}
        onActionSubmit={isCarpoolActive && deactivationBlocked ? undefined : () => handleActivationConfirmation(true)}
      />

      {isCarpoolActive && !isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', mb: 2, gap: 2 }}>
          <InputMobileComponent
            type='number'
            labeltext='Maximum Registrations'
            helperText={
              isInvalid ? `Set a value between ${MIN_REQUIRED_REGISTRATIONS} and ${MAX_ALLOWED_REGISTRATIONS}` : ''
            }
            value={maxRegistrations}
            min={MIN_REQUIRED_REGISTRATIONS}
            max={MAX_ALLOWED_REGISTRATIONS}
            onChange={(v: string) => handleMaxLimitSubmit(Number(v))}
            isError={isInvalid}
          />
          {hasChanges && (
            <ButtonMobileComponent text='Save' width='w-[100px]' disabled={isInvalid} onClick={handleSave} />
          )}
        </Box>
      )}
    </SettingsBox>
  )
}

export default ServiceActivationSettings
